import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import Iconify from '@src/components/iconify';
import Scrollbar from '@src/components/scrollbar';
import { ReferralStatusChip, RewardStatusChip } from '../StatusChip';
import { ReferredUser, ReferrerDetails as ReferrerDetailsType } from '../types';

const MOCK_REFERRER: ReferrerDetailsType = {
  userId: 123,
  displayName: 'user123',
  emailAddress: 'user123@email.com',
  referralCode: 'REF-USER123',
  registrationDate: '09-15-2024 08:30 AM',
  lastActive: '10-06-2024 04:55 PM',
  isActive: true,
  totalReferredUsers: 12,
  qualifiedReferrals: 8,
  inactiveReferrals: 4,
  silverCoins: 1200000,
  goldCoins: 0,
};

const MOCK_REFERRED: ReferredUser[] = [
  { userId: 781, displayName: 'LuckyPlay',  referredBy: 'user123', referredById: 123, registrationDate: '10-01-2024 10:24 AM', status: 'Qualified', hasPurchased: true,  rewardStatus: 'Claimed' },
  { userId: 842, displayName: 'ReelHero',   referredBy: 'user123', referredById: 123, registrationDate: '10-05-2024 01:24 PM', status: 'Inactive',  hasPurchased: false, rewardStatus: '-' },
  { userId: 955, displayName: 'SpinMaster', referredBy: 'user123', referredById: 123, registrationDate: '10-07-2024 09:10 AM', status: 'Qualified', hasPurchased: true,  rewardStatus: 'Claimed' },
];

const STATS = (r: ReferrerDetailsType) => [
  { title: 'Qualified Referrals',  subtitle: 'Rewards Issued',      value: r.qualifiedReferrals,             icon: 'mdi:check-outline',     color: '#2CB67D', bg: '#E8F5F0' },
  { title: 'Inactive Referrals',   subtitle: 'Registered (Inactive)', value: r.inactiveReferrals,            icon: 'mdi:account-outline',   color: '#E74C3C', bg: '#FDECEA' },
  { title: 'Total Bonus Coins',    subtitle: 'Awarded (Silver)',     value: r.silverCoins.toLocaleString(),   icon: 'mdi:alpha-s-circle',    color: '#3B82F6', bg: '#DBEAFE' },
  { title: 'Total Bonus Coins',    subtitle: 'Awarded (Gold)',       value: r.goldCoins.toLocaleString(),     icon: 'mdi:alpha-g-circle',    color: '#F59E0B', bg: '#FEF3C7' },
];

export default function ReferrerDetails() {
  useParams();
  const navigate = useNavigate();
  const referrer = MOCK_REFERRER;
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  return (
    <Container maxWidth="xl">
      <Helmet><title>Referrer Details</title></Helmet>

      {/* Back */}
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <Iconify icon="mdi:arrow-left" width={20} />
        </IconButton>
        <Typography variant="body2" color="text.secondary">Go Back</Typography>
      </Stack>

      {/* Profile Card */}
      <Card sx={{ p: 3, borderRadius: '16px', mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Grid container alignItems="center">
          {/* Left: Avatar + basic info */}
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
                backgroundColor: '#EEF1FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Iconify icon="mdi:account-plus-outline" width={30} sx={{ color: '#5B7FFF' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#607FAE' }}>User ID</Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#080A11' }}>{referrer.userId}</Typography>
                  <Chip
                    label={referrer.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      backgroundColor: referrer.isActive ? '#E8F5E9' : '#FDECEA',
                      color: referrer.isActive ? '#2E7D32' : '#C62828',
                      fontWeight: 600, fontSize: '0.7rem',
                    }}
                  />
                </Stack>
                <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Display Name</Typography>
                <Typography variant="body2" fontWeight={500} mb={0.5}>{referrer.displayName}</Typography>
                <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Email Address</Typography>
                <Typography variant="body2" fontWeight={500}>{referrer.emailAddress}</Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Middle: Referral Code + dates */}
          <Grid item xs={12} md={4} sx={{ borderLeft: { md: '1px solid #EEEEEE' }, pl: { md: 3 } }}>
            <Box mb={1.5}>
              <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Referral Code</Typography>
              <Stack
                direction="row" alignItems="center" justifyContent="space-between"
                sx={{ mt: 0.5, px: 1.5, py: 0.8, backgroundColor: '#F4F6FF', borderRadius: '8px', maxWidth: 240 }}
              >
                <Typography variant="body2" fontWeight={600} color="#3B5BDB">{referrer.referralCode}</Typography>
                <Iconify icon="mdi:arrow-top-right" width={16} sx={{ color: '#3B5BDB', ml: 1 }} />
              </Stack>
            </Box>
            <Box mb={1.5}>
              <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Registration Date</Typography>
              <Typography variant="body2" fontWeight={500}>{referrer.registrationDate}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Last Active</Typography>
              <Typography variant="body2" fontWeight={500}>{referrer.lastActive}</Typography>
            </Box>
          </Grid>

          {/* Right: Stats */}
          <Grid item xs={12} md={4} sx={{ pl: { md: 3 } }}>
            <Stack spacing={1.5}>
              <Box sx={{ backgroundColor: '#EEF1FF', borderRadius: '12px', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Iconify icon="mdi:account-group-outline" width={28} sx={{ color: '#5B7FFF' }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Total Referred Users</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#080A11' }}>{referrer.totalReferredUsers}</Typography>
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#F5EEFF', borderRadius: '12px', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Iconify icon="mdi:cart-outline" width={28} sx={{ color: '#A44CE8' }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Total Purchases (Referred Users)</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#080A11' }}>
                    {referrer.qualifiedReferrals} ({((referrer.qualifiedReferrals / referrer.totalReferredUsers) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        {STATS(referrer).map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ p: 2.5, borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: stat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Iconify icon={stat.icon} width={26} sx={{ color: stat.color }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#718EBF' }}>{stat.title}</Typography>
                  <Typography variant="caption" sx={{ color: '#9BA5B4' }} display="block">{stat.subtitle}</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#080A11', mt: 0.3 }}>{stat.value}</Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Referred Users Table */}
      <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" p={2.5}>
          <Typography variant="subtitle1" fontWeight={700}>Referred Users</Typography>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#6A00AC', borderRadius: '8px', textTransform: 'none', px: 3 }}
          >
            Export
          </Button>
        </Stack>

        <Scrollbar>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: '#5B7FFF', backgroundColor: '#FAFAFA', borderBottom: 'none' } }}>
                  <TableCell>User ID</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Registration Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Purchase</TableCell>
                  <TableCell>Reward Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_REFERRED.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.userId} sx={{ '& td': { borderBottom: '1px solid #F5F5F5', py: 2.5 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="#5B7FFF">
                        {row.userId}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{row.displayName}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{row.registrationDate}</Typography></TableCell>
                    <TableCell><ReferralStatusChip status={row.status} /></TableCell>
                    <TableCell><Typography variant="body2">{row.hasPurchased ? 'Yes' : 'No'}</Typography></TableCell>
                    <TableCell><RewardStatusChip status={row.rewardStatus} /></TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', borderColor: '#5B7FFF', color: '#5B7FFF' }}
                        onClick={() => navigate(`/dashboard/refer-a-friend/referred-user/${row.userId}`)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          component="div"
          count={MOCK_REFERRED.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={() => {}}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Card>
    </Container>
  );
}
